import { lazy, Suspense } from 'react';
import { ParticlesBackground } from './ParticlesBackground';
import { useSessionStore } from '../stores/sessionStore';

const NebulaWaveBackground = lazy(() =>
  import('./NebulaWaveBackground').then((module) => ({ default: module.NebulaWaveBackground }))
);

interface PageBackgroundProps {
  /** Show the ambient glow effect (forced if using props, otherwise respects store) */
  showOrb?: boolean;
  /** Custom color scheme */
  orbVariant?: 'purple-blue' | 'purple-pink' | 'blue-cyan';
}

/**
 * Unified page background with particles and ambient ocean glow.
 * Creates an "otherworldly glowing sea surface" aesthetic.
 */
export function PageBackground({ showOrb, orbVariant = 'purple-blue' }: PageBackgroundProps) {
  const backgroundStyle = useSessionStore((state) => state.backgroundStyle);

  if (backgroundStyle === 'nebula') {
    return (
      <Suspense fallback={null}>
        <NebulaWaveBackground />
      </Suspense>
    );
  }
  
  // Respect prop if provided, otherwise fallback to store
  const effectiveShowOrb = showOrb !== undefined ? showOrb : backgroundStyle === 'cosmic';

  // Cosmic mode uses clean stars (orbs handle the glow), Classic mode uses the old mesh
  const showMesh = backgroundStyle === 'stars';

  // Vibrant "sea in space" palettes - balanced for text readability
  const palettes = {
    'purple-blue': {
      deep: 'rgba(120, 0, 180, 0.25)',        // Was 0.45
      glow: 'rgba(0, 210, 255, 0.22)',        // Was 0.4
      accent: 'rgba(255, 0, 150, 0.18)',      // Was 0.35
      mid: 'rgba(80, 60, 220, 0.2)',          // Was 0.35
      highlight: 'rgba(0, 255, 200, 0.15)',   // Was 0.25
    },
    'purple-pink': {
      deep: 'rgba(130, 0, 200, 0.22)',
      glow: 'rgba(255, 50, 180, 0.2)',
      accent: 'rgba(200, 0, 255, 0.18)',
      mid: 'rgba(255, 100, 200, 0.16)',
      highlight: 'rgba(255, 150, 255, 0.12)',
    },
    'blue-cyan': {
      deep: 'rgba(20, 40, 180, 0.22)',
      glow: 'rgba(0, 230, 255, 0.22)',
      accent: 'rgba(0, 180, 220, 0.18)',
      mid: 'rgba(50, 100, 255, 0.18)',
      highlight: 'rgba(100, 255, 255, 0.15)',
    },
  };

  const c = palettes[orbVariant];

  return (
    <>
      {/* Particle effect layer - the "bioluminescent plankton" */}
      <ParticlesBackground showMesh={showMesh} />
      
      {/* Cosmic Ocean Glow - vibrant nebula layers */}
      {effectiveShowOrb && (
        <div className="fixed inset-0 pointer-events-none select-none z-0 overflow-hidden">
          
          {/* Deep purple abyss - emanating from bottom, shifted left */}
          <div 
            className="absolute -bottom-[40%] left-[30%] -translate-x-1/2 w-[200%] h-[80%] rounded-[100%] blur-[150px] animate-pulse-slow will-change-transform"
            style={{ background: c.deep }}
          />
          
          {/* Electric cyan bioluminescence - center-right */}
          <div 
            className="absolute top-[20%] right-[5%] w-[90%] h-[65%] rounded-[100%] blur-[120px] animate-drift will-change-transform"
            style={{ background: c.glow }}
          />
          
          {/* Hot magenta nebula accent - FAR left edge (mostly off-screen) */}
          <div 
            className="absolute top-[30%] -left-[45%] w-[70%] h-[55%] rounded-[100%] blur-[130px] animate-float will-change-transform"
            style={{ background: c.accent }}
          />
          
          {/* Electric indigo - center area */}
          <div 
            className="absolute top-[10%] left-[15%] w-[80%] h-[50%] rounded-[100%] blur-[100px] will-change-transform"
            style={{ background: c.mid }}
          />
          
          {/* Bright teal highlight - center-left top glow */}
          <div 
            className="absolute -top-[15%] left-[20%] w-[70%] h-[45%] rounded-[100%] blur-[100px] animate-pulse-slow will-change-transform"
            style={{ background: c.highlight }}
          />
          
          {/* Subtle purple wisps - center-right area */}
          <div 
            className="absolute top-[50%] left-[45%] w-[50%] h-[35%] rounded-[100%] blur-[90px] animate-drift will-change-transform"
            style={{ background: 'rgba(120, 0, 180, 0.15)' }}
          />
          
        </div>
      )}
    </>
  );
}
